import { Component, inject } from '@angular/core';

import { ReactiveFormsModule, FormGroup } from '@angular/forms';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { TranslateModule } from '@ngx-translate/core';
import { SasSelectComponent } from '@shared/sas-select/sas-select.component';
import { FieldGroup } from '@shared/interfaces/field-group';

const READ_MODE_CSSCLASS = 'textmode';

@Component({
    selector: 'sas-sub-form',
    standalone: true,
    imports: [ReactiveFormsModule, FormlyModule, TranslateModule, SasSelectComponent],
    templateUrl: './sas-sub-form.component.html',
    styleUrl: './sas-sub-form.component.scss'
})

export class SasSubFormComponent extends FieldType {

    readMode!: any;
    optionSelected: any = null;
    unique: number = 0;
    multiple: boolean = false; // props <=> templateOptions
    forms: FormGroup[] = [];
    fields: Array<any> = [];
    fieldsSelected: Array<any> = [];
    fieldsMapped: Map<number, FieldGroup> = new Map();
    btnText: string = '';

    constructor() {
        super();
    }

    ngOnInit() {
        this.readMode = this.formState.readMode ?? false;
        this.multiple = this.field.props?.['multiple'] ?? false; // props <=> templateOptions
        this.fields = this.field.props?.['fields'] ?? [];
        this.fieldsSelected = [];
        this.btnText = this.field.props?.['btnText'] ?? '';
        // on submitted, reset it
        this.model[this.getKey(this.field)].forEach((el: any, ind: number) => {
            this.forms[ind] = new FormGroup({});
        })
    }

    selectOption(opt:number) {
        if (!this.multiple) {
            let index = this.getIndexFromOpt(opt, this.fields);
            this.addNew(index);
        }
    };

    getIndexFromOpt(value: any, arr: Array<any>): number {
        return arr.findIndex((elem) => value === elem.fieldGroupId);
    }

    addNew(ind: number = -1) {
        let index = (ind >= 0)
            ? ind
            : this.getIndexFromOpt(this.optionSelected, this.fields);

        if (index >= 0) {
            this.model[`${this.field.key}`] = this.model[`${this.field.key}`] || [];
            this.fieldsSelected = structuredClone(this.fields[index].fieldGroup);
            let repeatSection = this.model[`${this.field.key}`];
            let newSection = { option: this.optionSelected, data: {} };
            if (this.multiple) {
                repeatSection.push(newSection);
                this.forms.push(new FormGroup({}));
            }
            else {
                repeatSection.splice(0, repeatSection.length, newSection);
            }
            this.model[`${this.field.key}`] = repeatSection;
        }
        else {
            console.warn('Option not found to add the fieldgroup.');
        }
    }

    getCachedFields(option: string, index: number): Array<any> {
        let cachedFields = this.fieldsMapped.get(index);
        let copiedFields!: Array<any>;
        if(!this.fieldsMapped.has(index)) {
            copiedFields =  this.copyFields(option); 
            this.fieldsMapped.set(index, { fields: copiedFields, option: option });
        }
        else if (!this.multiple && this.fieldsMapped.has(index) && this.optionSelected !== null && cachedFields?.option !== this.optionSelected) {
            copiedFields = this.copyFields(option);
            this.fieldsMapped.set(index, { fields: this.copyFields(option), option: this.optionSelected });
        }
        return this.fieldsMapped.get(index)?.fields ?? [] ;
    }

    copyFields(elemOption: any = null) {
        let fieldsSection = [];
        let option = (this.optionSelected === undefined || this.optionSelected === null)
            ? elemOption
            : this.optionSelected;

        if (option !== undefined && option !== null) {
            let index = this.getIndexFromOpt(option, this.fields);
            if (index >= 0) {
                fieldsSection = structuredClone(this.fields[index].fieldGroup);
                this.addRandomIds(fieldsSection);
                if (this.readMode) {
                    this.setReadMode(fieldsSection)
                }
            }
        }
        return fieldsSection;
    }


    getKey(obj: any): string {
        return obj.key;
    }

    addRandomIds(fields: Array<any>) {
        this.unique++;
        fields.forEach((field: any, index: number) => {            
            if (field.fieldGroup) {
                this.addRandomIds(field.fieldGroup);
                return; // fieldGroups don't need an ID
            }
            if (field.props && field.props.fields) {
                this.addRandomIds(field.props.fields);
            }
            field.id = field.id || (field.key + '_' + index + '_' + this.unique + this.getRandomInt(0, 9999));
            field.name = field.id; // overwrite the name as on repeatition we cannot allow repeated names        
        });
    }

    setReadMode(fields: Array<any>) {
        fields.forEach((field:any, index:number) => {
            if (field.fieldGroup) {
                this.setReadMode(field.fieldGroup);
                return;
            }
            if (field.props && field.props.fields) {
                this.setReadMode(field.props.fields);
            }
            if (field.props.sasClass === undefined) {
                field.props.sasClass = '';
            }
            field.templateOptions.sasClass += ' ' + READ_MODE_CSSCLASS;
        });
    }

    getRandomInt(min: number, max: number) {
        return Math.floor(Math.random() * (max - min)) + min;
    }

    resetOption(ev: any) {
        this.optionSelected = null;
    }

    getSelectOptions(options: any): Array<any> {
        return options;
    }

    removeSection(ind: number) {
        let key = this.getKey(this.field);
        this.model[key].splice(ind, 1);
        this.fieldsMapped.delete(ind);
    }
}
